import { CheckCircle, AlertTriangle, XCircle, Loader2 } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/ui/components/primitives/tooltip";
import { cn } from "@/ui/components/primitives/utils";

interface PSPHealthIndicatorProps {
  status: "HEALTHY" | "DEGRADED" | "DOWN" | "UNKNOWN";
  responseTime?: number;
  successRate?: number;
  lastChecked?: string;
  className?: string;
}

export function PSPHealthIndicator({
  status,
  responseTime,
  successRate,
  lastChecked,
  className,
}: PSPHealthIndicatorProps) {
  const getIcon = () => {
    switch (status) {
      case "HEALTHY":
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case "DEGRADED":
        return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      case "DOWN":
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Loader2 className="h-4 w-4 text-gray-400 animate-spin" />;
    }
  };

  const getTooltipContent = () => {
    if (status === "UNKNOWN") {
      return "Health status unknown";
    }

    return (
      <div className="space-y-1">
        <div className="font-medium">Health Status: {status}</div>
        {responseTime !== undefined && (
          <div className="text-xs">Response Time: {responseTime}ms</div>
        )}
        {successRate !== undefined && (
          <div className="text-xs">Success Rate: {(successRate * 100).toFixed(1)}%</div>
        )}
        {lastChecked && (
          <div className="text-xs text-muted-foreground">
            Last checked: {new Date(lastChecked).toLocaleString()}
          </div>
        )}
      </div>
    );
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className={cn("inline-flex items-center", className)}>
            {getIcon()}
          </div>
        </TooltipTrigger>
        <TooltipContent>
          {getTooltipContent()}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

interface PSPHealthBadgeProps {
  status: "HEALTHY" | "DEGRADED" | "DOWN" | "UNKNOWN";
  className?: string;
}

export function PSPHealthBadge({ status, className }: PSPHealthBadgeProps) {
  const variants = {
    HEALTHY: "bg-green-500/10 text-green-700 border-green-500/20 dark:text-green-400",
    DEGRADED: "bg-yellow-500/10 text-yellow-700 border-yellow-500/20 dark:text-yellow-400",
    DOWN: "bg-red-500/10 text-red-700 border-red-500/20 dark:text-red-400",
    UNKNOWN: "bg-gray-500/10 text-gray-700 border-gray-500/20 dark:text-gray-400",
  };

  return (
    <span className={cn("inline-flex items-center gap-1 px-2 py-1 rounded text-xs border", variants[status], className)}>
      {status === "HEALTHY" && <CheckCircle className="h-3 w-3" />}
      {status === "DEGRADED" && <AlertTriangle className="h-3 w-3" />}
      {status === "DOWN" && <XCircle className="h-3 w-3" />}
      {status === "UNKNOWN" && <Loader2 className="h-3 w-3 animate-spin" />}
      {status}
    </span>
  );
}
